import React, { forwardRef, useCallback } from 'react';
import { ImageSourcePropType } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

import { TableItem } from '../../../store/types';
import { STAGE_WHEEL_POSE } from '../common/carouselStage';
import { WheelCarousel, WheelCarouselHandle } from '../common/WheelCarousel';
import { TableCard } from '../LobbyTable/TableCard';

export type RoomTablesCarouselHandle = WheelCarouselHandle;

type Props = {
	tables: TableItem[];
	roomSlug?: string;
	activeIndex: number;
	onActiveIndexChange: (index: number) => void;
	width: number;
	height: number;
	cardWidth: number;
	cardHeight: number;
	progress?: SharedValue<number>;
	/** Passed to every card; see TableCard. */
	onVipRequired?: () => void;
	/** 'stage': the mobile carousel stage's card and pose (common/carouselStage). */
	shape?: 'tall' | 'stage';
	/** Passed to every card; see TableCard. */
	cardBackground?: ImageSourcePropType;
};

const RoomTablesCarouselInner = (
	{
		tables,
		roomSlug,
		activeIndex,
		onActiveIndexChange,
		width,
		height,
		cardWidth,
		cardHeight,
		progress,
		onVipRequired,
		shape = 'tall',
		cardBackground,
	}: Props,
	ref: React.Ref<RoomTablesCarouselHandle>,
) => {
	const renderItem = useCallback(
		({ item, index }: { item: TableItem; index: number }) => (
			<TableCard
				table={item}
				roomSlug={roomSlug}
				index={index}
				totalCount={tables.length}
				cardWidth={cardWidth}
				cardHeight={cardHeight}
				progress={progress}
				framed
				onVipRequired={onVipRequired}
				shape={shape}
				background={cardBackground}
			/>
		),
		[
			cardBackground,
			cardHeight,
			cardWidth,
			onVipRequired,
			progress,
			roomSlug,
			shape,
			tables.length,
		],
	);

	return (
		<WheelCarousel
			ref={ref}
			data={tables}
			renderItem={renderItem}
			activeIndex={activeIndex}
			onActiveIndexChange={onActiveIndexChange}
			width={width}
			height={height}
			cardWidth={cardWidth}
			cardHeight={cardHeight}
			progress={progress}
			{...(shape === 'stage' ? { pose: STAGE_WHEEL_POSE, itemWidthScale: 1 } : null)}
		/>
	);
};

export const RoomTablesCarousel = forwardRef(RoomTablesCarouselInner);

export default RoomTablesCarousel;
